import React, { useCallback, useState } from "react";
import { useMutation, graphql, useLazyLoadQuery } from "react-relay";
import { LazyQueryNetworkBoundrary } from "../../../../../components/mobile/QueryNetworkBoundrary";
import { UserLoggedInNavigatorScreenProps } from "../../../../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";

import {
  WidgetTaskCollectionContext,
  WidgetTaskCollectionTask,
} from "./WidgetTaskCollectionContext";
import { WidgetTaskCollectionPreview } from "./WidgetTaskCollectionPreview";
import { WidgetTaskCollectionSettingsNavigator } from "./WidgetTaskCollectionSettings/WidgetTaskCollectionSettingsNavigator";
import { WSTaskCollectionMutation } from "./__generated__/WSTaskCollectionMutation.graphql";
import { WSTaskCollectionQuery } from "./__generated__/WSTaskCollectionQuery.graphql";
import { withWebModal } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModal";
import { WidgetScreenPreview } from "../WidgetScreenPreview/WidgetScreenPreview";
import { WidgetScreen } from "../../common/WidgetScreen";

export type WSTaskCollectionParams = object;

export const WSTaskCollection = withWebModal(WSTaskCollectionC, true);
function WSTaskCollectionC(
  props: UserLoggedInNavigatorScreenProps<"WSTaskCollection">
) {
  return (
    <LazyQueryNetworkBoundrary>
      {(fetchKey) => <InnerWSTaskCollection {...props} fetchKey={fetchKey} />}
    </LazyQueryNetworkBoundrary>
  );
}

const WS_TASK_COLLECTION_QUERY = graphql`
  query WSTaskCollectionQuery($widgetId: ID!) {
    widgetTaskCollection(id: $widgetId) {
      id
      title
      taskSections {
        id
        section {
          id
          title
        }
        task {
          id
          title
          routine {
            id
            title
          }
        }
      }
    }
  }
`;

function InnerWSTaskCollection(
  props: UserLoggedInNavigatorScreenProps<"WSTaskCollection"> & {
    fetchKey: string;
  }
) {
  const data = useLazyLoadQuery<WSTaskCollectionQuery>(
    WS_TASK_COLLECTION_QUERY,
    {
      widgetId: props.route.params.widgetId,
    },
    { fetchKey: props.fetchKey, fetchPolicy: "store-and-network" }
  );

  const [title, setTitle] = useState<string>(
    data.widgetTaskCollection?.title ?? ""
  );
  const [selectedTasks, setSelectedTasks] = useState<
    WidgetTaskCollectionTask[]
  >(
    data.widgetTaskCollection?.taskSections.map((ts, index) => ({
      id: ts.task.id,
      ordinal: index,
      sectionId: ts.section.id,
      title: ts.task.title,
      sectionTitle: ts.section.title ?? "",
      routineTitle: ts.task.routine.title,
    })) ?? []
  );

  const [save, loading] = useMutation<WSTaskCollectionMutation>(graphql`
    mutation WSTaskCollectionMutation(
      $storecastId: ID!
      $widgetId: ID!
      $update: WidgetTaskCollectionUpdateInput!
    ) {
      dashboard(dashboardId: $storecastId) {
        updateWidgetTaskCollection(id: $widgetId, update: $update) {
          id
        }
      }
    }
  `);

  const onSave = useCallback(() => {
    save({
      variables: {
        storecastId: props.route.params.storecastId,
        widgetId: props.route.params.widgetId,
        update: {
          setTasks: selectedTasks
            .slice()
            .sort((a, b) => (a.ordinal > b.ordinal ? 1 : -1))
            .map((t) => ({
              taskId: t.id,
              sectionId: t.sectionId,
            })),
          setTitle: title,
        },
      },
      onCompleted: () => {
        props.navigation.goBack();
      },
    });
  }, [
    props.navigation,
    props.route.params.storecastId,
    props.route.params.widgetId,
    save,
    selectedTasks,
    title,
  ]);

  return (
    <WidgetTaskCollectionContext.Provider
      value={{ selectedTasks, setSelectedTasks, setTitle, title }}
    >
      <WidgetScreen
        preview={
          <WidgetScreenPreview
            loading={loading}
            unitSize={props.route.params.unitSize}
            onSave={onSave}
            size={props.route.params.size}
          >
            <WidgetTaskCollectionPreview
              storecastId={props.route.params.storecastId}
            />
          </WidgetScreenPreview>
        }
        settings={
          <WidgetTaskCollectionSettingsNavigator
            storecastId={props.route.params.storecastId}
            onCancel={props.navigation.goBack}
          />
        }
      />
    </WidgetTaskCollectionContext.Provider>
  );
}
